import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useOrders, OrderItem } from './OrderContext';
import { db } from '../config/firebase';
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
} from 'firebase/firestore';
import { logger } from '../utils/logger';
import { COLLECTIONS } from '../config/constants';

// Abonelik aralıkları
export type SubscriptionInterval = 'weekly' | 'biweekly' | 'monthly';

// Abonelik durumları
export type SubscriptionStatus = 'active' | 'paused' | 'cancelled';

// Abonelik veri tipi
export interface Subscription {
  id: string;
  userId: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  items: OrderItem[];
  interval: SubscriptionInterval;
  nextDeliveryDate: string; // YYYY-MM-DD
  preferredSlot?: string; // Örn: 10:00-12:00
  paymentMethod: 'cash_on_delivery' | 'credit_card';
  status: SubscriptionStatus;
  totalAmount: number;
  createdAt: string;
  updatedAt: string;
  lastOrderDate?: string;
  orderCount: number; // Toplam sipariş sayısı
}

// Context tipi
interface SubscriptionContextType {
  subscriptions: Subscription[];
  loading: boolean;
  
  // CRUD işlemleri
  createSubscription: (data: Omit<Subscription, 'id' | 'createdAt' | 'updatedAt' | 'orderCount' | 'lastOrderDate'>) => Promise<string | null>;
  updateSubscription: (id: string, data: Partial<Subscription>) => Promise<boolean>;
  pauseSubscription: (id: string) => Promise<boolean>;
  resumeSubscription: (id: string) => Promise<boolean>;
  cancelSubscription: (id: string) => Promise<boolean>;
  deleteSubscription: (id: string) => Promise<boolean>;
  
  // Yardımcı fonksiyonlar
  getActiveSubscriptions: () => Subscription[];
  getSubscriptionById: (id: string) => Subscription | undefined;
  getUserSubscriptions: (userId: string) => Subscription[];
  
  // Otomatik sipariş işleme (Admin için)
  processSubscriptionOrder: (subscriptionId: string) => Promise<boolean>;
  getDueSubscriptions: () => Subscription[]; // Bugün siparişi olan abonelikler
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscriptions = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscriptions must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: ReactNode;
}

// Bir sonraki teslimat tarihini hesapla
const calculateNextDeliveryDate = (currentDate: string, interval: SubscriptionInterval): string => {
  const date = new Date(currentDate);
  
  switch (interval) {
    case 'weekly':
      date.setDate(date.getDate() + 7);
      break;
    case 'biweekly':
      date.setDate(date.getDate() + 14);
      break;
    case 'monthly':
      date.setMonth(date.getMonth() + 1);
      break;
  }
  
  return date.toISOString().split('T')[0];
};

// Aralık etiketleri
export const getIntervalLabel = (interval: SubscriptionInterval): string => {
  switch (interval) {
    case 'weekly': return 'Haftalık';
    case 'biweekly': return '2 Haftada Bir';
    case 'monthly': return 'Aylık';
    default: return interval;
  }
};

// Durum etiketleri
export const getStatusLabel = (status: SubscriptionStatus): string => {
  switch (status) {
    case 'active': return 'Aktif';
    case 'paused': return 'Duraklatıldı';
    case 'cancelled': return 'İptal Edildi';
    default: return status;
  }
};

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const { addOrder } = useOrders();
  const isAdmin = currentUser?.role?.toLowerCase() === 'admin';

  // Firestore realtime listener
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!currentUser) {
      setSubscriptions([]);
      setLoading(false);
      return;
    }

    const colRef = collection(db, COLLECTIONS.SUBSCRIPTIONS);
    // Admin tüm abonelikleri görebilir, müşteri sadece kendininkini
    const colQuery = isAdmin 
      ? colRef 
      : query(colRef, where('userId', '==', currentUser.uid));

    const unsub = onSnapshot(
      colQuery,
      (snap) => {
        const list: Subscription[] = snap.docs.map((d) => ({
          id: d.id,
          ...d.data()
        } as Subscription));
        
        setSubscriptions(list.sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ));
        setLoading(false);
      },
      (error) => {
        logger.error('Subscription dinleme hatası:', error);
        setSubscriptions([]);
        setLoading(false);
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, isAdmin]);

  // Abonelik oluştur
  const createSubscription = useCallback(async (
    data: Omit<Subscription, 'id' | 'createdAt' | 'updatedAt' | 'orderCount' | 'lastOrderDate'>
  ): Promise<string | null> => {
    if (!currentUser) return null;

    try {
      const now = new Date().toISOString();
      const subscriptionData = {
        ...data,
        userId: currentUser.uid,
        createdAt: now,
        updatedAt: now,
        orderCount: 0,
      };

      const ref = await addDoc(collection(db, COLLECTIONS.SUBSCRIPTIONS), subscriptionData);
      return ref.id;
    } catch (error) {
      logger.error('Abonelik oluşturma hatası:', error);
      return null;
    }
  }, [currentUser]);

  // Abonelik güncelle
  const updateSubscription = useCallback(async (
    id: string, 
    data: Partial<Subscription>
  ): Promise<boolean> => {
    try {
      const updateData = {
        ...data,
        updatedAt: new Date().toISOString(),
      };
      // id'yi çıkar
      delete (updateData as any).id;
      
      await updateDoc(doc(db, COLLECTIONS.SUBSCRIPTIONS, id), updateData);
      return true;
    } catch (error) {
      logger.error('Abonelik güncelleme hatası:', error);
      return false;
    }
  }, []);

  // Aboneliği duraklat
  const pauseSubscription = useCallback(async (id: string): Promise<boolean> => {
    return updateSubscription(id, { status: 'paused' });
  }, [updateSubscription]);

  // Aboneliği devam ettir
  const resumeSubscription = useCallback(async (id: string): Promise<boolean> => {
    const subscription = subscriptions.find(s => s.id === id);
    if (!subscription) return false;

    // Eğer nextDeliveryDate geçmişte kaldıysa, bugünden itibaren yeniden hesapla
    const today = new Date().toISOString().split('T')[0];
    let nextDate = subscription.nextDeliveryDate;
    
    if (nextDate < today) {
      nextDate = calculateNextDeliveryDate(today, subscription.interval);
    }

    return updateSubscription(id, { 
      status: 'active',
      nextDeliveryDate: nextDate
    });
  }, [subscriptions, updateSubscription]);

  // Aboneliği iptal et
  const cancelSubscription = useCallback(async (id: string): Promise<boolean> => {
    return updateSubscription(id, { status: 'cancelled' });
  }, [updateSubscription]);

  // Aboneliği sil
  const deleteSubscription = useCallback(async (id: string): Promise<boolean> => {
    try {
      await deleteDoc(doc(db, COLLECTIONS.SUBSCRIPTIONS, id));
      return true;
    } catch (error) {
      logger.error('Abonelik silme hatası:', error);
      return false;
    }
  }, []);

  // Aktif abonelikleri getir
  const getActiveSubscriptions = useCallback((): Subscription[] => {
    return subscriptions.filter(s => s.status === 'active');
  }, [subscriptions]);

  // ID ile abonelik getir
  const getSubscriptionById = useCallback((id: string): Subscription | undefined => {
    return subscriptions.find(s => s.id === id);
  }, [subscriptions]);

  // Kullanıcının aboneliklerini getir
  const getUserSubscriptions = useCallback((userId: string): Subscription[] => {
    return subscriptions.filter(s => s.userId === userId);
  }, [subscriptions]);

  // Bugün siparişi olan abonelikleri getir
  const getDueSubscriptions = useCallback((): Subscription[] => {
    const today = new Date().toISOString().split('T')[0];
    return subscriptions.filter(s => 
      s.status === 'active' && s.nextDeliveryDate <= today
    );
  }, [subscriptions]);

  // Abonelik siparişini işle (Admin)
  const processSubscriptionOrder = useCallback(async (subscriptionId: string): Promise<boolean> => {
    const subscription = subscriptions.find(s => s.id === subscriptionId);
    if (!subscription || subscription.status !== 'active') return false;

    try {
      // Sipariş oluştur
      const orderDetails = subscription.items.map(i => `${i.quantity}× ${i.name}`).join(', ');
      
      // addOrder void döndürüyor, hata durumunda exception fırlatır
      await (addOrder as (order: any) => Promise<void>)({
        customerId: subscription.userId,
        customerName: subscription.customerName,
        customerPhone: subscription.customerPhone,
        customerAddress: subscription.customerAddress,
        customerNotes: [`Otomatik abonelik siparişi (${getIntervalLabel(subscription.interval)})`],
        orderDetails,
        items: subscription.items,
        amount: subscription.totalAmount,
        paymentStatus: 'unpaid',
        paymentMethod: subscription.paymentMethod,
        deliveryStatus: 'preparing',
        distributor: 'Seçilmedi',
        deliveryDate: subscription.nextDeliveryDate,
        deliverySlot: subscription.preferredSlot || '',
      });

      // Aboneliği güncelle - sonraki tarihi hesapla
      const nextDate = calculateNextDeliveryDate(subscription.nextDeliveryDate, subscription.interval);
      
      await updateSubscription(subscriptionId, {
        nextDeliveryDate: nextDate,
        lastOrderDate: subscription.nextDeliveryDate,
        orderCount: subscription.orderCount + 1,
      });

      return true;
    } catch (error) {
      logger.error('Abonelik siparişi işleme hatası:', error);
      return false;
    }
  }, [subscriptions, addOrder, updateSubscription]);

  const contextValue = useMemo<SubscriptionContextType>(() => ({
    subscriptions,
    loading,
    createSubscription,
    updateSubscription,
    pauseSubscription,
    resumeSubscription,
    cancelSubscription,
    deleteSubscription,
    getActiveSubscriptions,
    getSubscriptionById,
    getUserSubscriptions,
    processSubscriptionOrder,
    getDueSubscriptions,
  }), [
    subscriptions,
    loading,
    createSubscription,
    updateSubscription,
    pauseSubscription,
    resumeSubscription,
    cancelSubscription,
    deleteSubscription,
    getActiveSubscriptions,
    getSubscriptionById,
    getUserSubscriptions,
    processSubscriptionOrder,
    getDueSubscriptions,
  ]);

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
